import { Alert, Box, Button, CircularProgress, Paper, Tab, Tabs, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { createModuleVersion, getModules, updateModule } from "../../../../actions/fgModuleActions";
import ModuleFormDialog from "../components/ModuleFormDialog";
import VersionDetailPanel from "../components/VersionDetailPanel";
import VersionFormDialog from "../components/VersionFormDialog";
import { versionStringToEnum } from "../utils/dataTransformer";

const TabPanel = (props) => {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`version-tabpanel-${index}`}
            aria-labelledby={`version-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
};

const ModuleDetail = () => {
    const { moduleId } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { modules, loading, error } = useSelector((state) => state.fgModules);
    const module = modules.find((m) => m.id === parseInt(moduleId, 10));

    const [tabValue, setTabValue] = useState(0);
    const [isVersionDialogOpen, setVersionDialogOpen] = useState(false);
    const [isModuleEditDialogOpen, setModuleEditDialogOpen] = useState(false);

    useEffect(() => {
        if (modules.length === 0) {
            dispatch(getModules());
        }
    }, [dispatch, modules.length]);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleSubmitVersion = (versionData) => {
        const payload = {
            ...versionData,
            version: versionStringToEnum(versionData.version),
        };
        dispatch(createModuleVersion(module.id, payload));
    };

    const handleUpdateModule = (moduleData) => {
        const payload = {
            moduleId: module.id,
            ...moduleData,
        };
        dispatch(updateModule(payload));
    };

    if (loading && !module) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">Fehler beim Laden der Moduldetails: {error}</Alert>;
    }

    if (!module) {
        return <Alert severity="warning">Modul nicht gefunden.</Alert>;
    }

    const starterVersion = module.versions.find((v) => v.version === "Starter");
    const proVersion = module.versions.find((v) => v.version === "Pro");
    const geniusVersion = module.versions.find((v) => v.version === "Genius");

    const existingVersions = module.versions.map((v) => v.version);
    const allPossibleVersions = ["Starter", "Pro", "Genius"];
    const availableVersions = allPossibleVersions.filter((v) => !existingVersions.includes(v));
    const starterFeatures = starterVersion?.features || [];
    const proFeatures = proVersion?.features || [];

    return (
        <>
            <Paper sx={{ margin: "auto", marginTop: 4, p: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Button onClick={() => navigate("/modules")}>Zurück zur Übersicht</Button>
                    {availableVersions.length > 0 && (
                        <Button variant="contained" onClick={() => setVersionDialogOpen(true)}>
                            Version hinzufügen
                        </Button>
                    )}
                </Box>
                <Typography variant="h4" gutterBottom>
                    {module.name}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {module.description}
                </Typography>

                <Button sx={{ mt: 2 }} variant="outlined" size="small" onClick={() => setModuleEditDialogOpen(true)}>
                    Moduldetails bearbeiten
                </Button>

                <Box sx={{ borderBottom: 1, borderColor: "divider", mt: 3 }}>
                    <Tabs value={tabValue} onChange={handleTabChange} aria-label="module versions tabs">
                        <Tab label="Starter" id="version-tab-0" />
                        <Tab label="Pro" id="version-tab-1" />
                        <Tab label="Genius" id="version-tab-2" />
                    </Tabs>
                </Box>

                <TabPanel value={tabValue} index={0}>
                    <VersionDetailPanel version={starterVersion} />
                </TabPanel>
                <TabPanel value={tabValue} index={1}>
                    <VersionDetailPanel version={proVersion} inheritedFeatures={starterFeatures} />
                </TabPanel>
                <TabPanel value={tabValue} index={2}>
                    <VersionDetailPanel
                        version={geniusVersion}
                        inheritedFeatures={[...starterFeatures, ...proFeatures]}
                    />
                </TabPanel>
            </Paper>
            <VersionFormDialog
                open={isVersionDialogOpen}
                onClose={() => setVersionDialogOpen(false)}
                onSubmit={handleSubmitVersion}
                availableVersions={availableVersions}
            />
            <ModuleFormDialog
                open={isModuleEditDialogOpen}
                onClose={() => setModuleEditDialogOpen(false)}
                onSubmit={handleUpdateModule}
                initialData={module}
            />
        </>
    );
};

export default ModuleDetail;
